import React from 'react';
import { Pressable } from 'react-native';
import Animated, {
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { UnistylesRuntime } from 'react-native-unistyles';

import { Box, HStack, Spinner, Text, VStack } from '@squire/ui';

import { TagIcon } from '@/components/_icons';
import { Card } from '@/components/_shared/Card';
import { CookieConsent } from '@/components/_shared/CookieConsent';
import { ORDER_SHEET_EASING } from '@/hooks/booking/useOrderSheetMotion';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { getCartRangePrice, getCartTotal } from '@/utils/bookingCartUtils';
import { isWeb } from '@/utils/getPlatform';
import { formatPrice } from '@/utils/internationalizationUtils';

import { HIDDEN_OFFSET, styles } from './styles';
import type { ViewOrderBarProps } from './types';

export const ViewOrderBar = ({
  isCreatingCart = false,
  style,
  testID = 'view-order-bar',
  stage = 'choose-service',
  selectedServicesCount = 0,
  onPress,
  cart,
  shop,
  amount,
  currency: currencyProp,
  waitlistSelectedIds,
  hasTipBeenUserSelected = false,
  isStandbyWaitlist = false,
  sheetIndex,
}: ViewOrderBarProps) => {
  const { t, i18n } = useScreenTranslations('ViewOrderBar');

  const currency =
    currencyProp || cart?.currency || shop?.brand?.currency || 'USD';
  const appointments = cart?.items || [];

  const showCookieConsent = isWeb() && UnistylesRuntime.breakpoint === 'mobile';

  const allAppointmentsHaveTimes =
    appointments.length > 0 &&
    appointments.every(
      (appointment) =>
        appointment.dateTime || waitlistSelectedIds?.has(appointment.id),
    );

  const translateY = useSharedValue(0);

  useAnimatedReaction(
    () => (sheetIndex?.value ?? -1) > -1,
    (isCovered, wasCovered) => {
      if (isCovered === wasCovered) return;
      translateY.value = isCovered
        ? HIDDEN_OFFSET
        : withTiming(0, { duration: 180, easing: ORDER_SHEET_EASING });
    },
  );

  const slideStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const totalPrice = cart
    ? getCartTotal(
        cart,
        stage === 'choose-time' &&
          allAppointmentsHaveTimes &&
          hasTipBeenUserSelected,
      )
    : null;
  const rangePrice = cart ? getCartRangePrice(cart) : null;
  const itemCount = cart?.items.length || 0;

  const formatItemCount = (count: number) => {
    return count === 1 ? t('itemCount.one') : t('itemCount.other', { count });
  };

  if (!cart && amount == null) {
    if (showCookieConsent) return <CookieConsent position="absolute" />;
    return null;
  }

  // Resolve the display price: direct amount, cart range, or cart total
  const displayPrice =
    amount != null
      ? formatPrice(amount, { locale: i18n.language, currency })
      : rangePrice
        ? `${formatPrice(rangePrice.min / 100, { locale: i18n.language, currency })} - ${formatPrice(rangePrice.max / 100, { locale: i18n.language, currency })}`
        : totalPrice
          ? formatPrice(totalPrice / 100, { locale: i18n.language, currency })
          : null;

  return (
    <VStack style={[styles.absolute, style]}>
      <Animated.View testID={testID} style={[styles.container, slideStyle]}>
        <Pressable
          testID="view-order-button"
          onPress={onPress}
          style={styles.content}
          accessibilityRole="button"
          accessibilityHint={t('accessibility.expandHint')}
          accessibilityLabel={
            isStandbyWaitlist
              ? t('continue')
              : cart
                ? t('accessibility.orderSummary', {
                    itemCount: formatItemCount(itemCount),
                    totalPrice: totalPrice
                      ? formatPrice(totalPrice, {
                          locale: i18n.language,
                          currency,
                        })
                      : undefined,
                  })
                : t('viewOrder')
          }
        >
          <HStack style={styles.orderSummary}>
            {stage === 'choose-service' &&
              !allAppointmentsHaveTimes &&
              appointments.length > 1 && (
                <Card style={styles.guestsCountCard}>
                  <Text variant="m" color="borderPrimary">
                    {appointments.length}
                  </Text>
                </Card>
              )}

            <Text
              variant="lStrong"
              color="cartBarTextPrimary"
              style={!allAppointmentsHaveTimes && styles.viewOrderText}
            >
              {isStandbyWaitlist
                ? t('continue')
                : allAppointmentsHaveTimes
                  ? t('goToCheckout')
                  : selectedServicesCount > 1
                    ? `${t('viewOrder')} (${selectedServicesCount})`
                    : t('viewOrder')}
            </Text>

            {isCreatingCart ? (
              <Spinner color="cartBarTextPrimary" />
            ) : (
              <>
                {displayPrice && !isStandbyWaitlist && (
                  <Text variant="lStrong" color="cartBarTextSecondary">
                    {displayPrice}
                  </Text>
                )}

                {cart?.promo && (
                  <Box style={styles.promoIconContainer}>
                    <TagIcon color="iconPositive" size={10} />
                  </Box>
                )}
              </>
            )}
          </HStack>
        </Pressable>
      </Animated.View>

      {showCookieConsent && (
        <CookieConsent position="relative" style={styles.cookieConsent} />
      )}
    </VStack>
  );
};
